export * from './lib/shared-types';
export * from './lib/gradients';
export * from './lib/objectDefinition';
export * from './lib/printTemplate';

export enum GroupType {
  Default = 'DEFAULT',
  Floor = 'FLOOR',
  Surface = 'SURFACE',
  Room = 'ROOM',
}

/**
 * Room folder (ROOM group) identifier scheme — the ONE owner of the
 * `roomgroup_` prefix convention, shared by the client (folder creation and
 * reconciliation) and the server (room-measure delete cascades its auto
 * folder). Auto folders derive their id from the room annotation so the
 * room↔folder mapping stays computable and creation stays idempotent; manual
 * folders use the `roomgroup_manual_` prefix so reconciliation never touches
 * them.
 */
const ROOM_FOLDER_AUTO_PREFIX = 'roomgroup_';
const ROOM_FOLDER_MANUAL_PREFIX = 'roomgroup_manual_';

/** Deterministic folder id for a room annotation's auto-created folder. */
export function roomFolderIdForRoom(roomAnnotationId: string): string {
  return `${ROOM_FOLDER_AUTO_PREFIX}${roomAnnotationId}`;
}

/** Folder id for a manually created (polygon-less) room folder. */
export function manualRoomFolderId(uuid: string): string {
  return `${ROOM_FOLDER_MANUAL_PREFIX}${uuid}`;
}

/**
 * The room annotation id an AUTO folder id was derived from, or null for
 * manual folders and non-folder identifiers.
 */
export function roomIdFromAutoFolderId(folderId: string): string | null {
  if (!folderId.startsWith(ROOM_FOLDER_AUTO_PREFIX) || folderId.startsWith(ROOM_FOLDER_MANUAL_PREFIX)) return null;
  return folderId.slice(ROOM_FOLDER_AUTO_PREFIX.length);
}

/**
 * Enum distinguishing between different kinds of surfaces
 * - SURFACE: A regular surface
 * - SECTION: A cross-section cut through the point cloud
 */
export enum SurfaceKind {
  SURFACE = 'SURFACE',
  SECTION = 'SECTION',
}

/**
 * Subtype of an OPENING annotation.
 * - DOOR: door opening
 * - WINDOW: window opening
 */
export enum OpeningKind {
  DOOR = 'DOOR',
  WINDOW = 'WINDOW',
}

/**
 * Door subtype, driving the 2D floor-plan glyph for a DOOR opening. Mirrors the
 * standard architectural door symbols. Ignored for WINDOW openings.
 */
export enum DoorType {
  SINGLE = 'SINGLE',
  DOUBLE = 'DOUBLE',
  BIFOLD = 'BIFOLD',
  SLIDING = 'SLIDING',
  BYPASS_SLIDING = 'BYPASS_SLIDING',
  POCKET = 'POCKET',
  SURFACE_SLIDING = 'SURFACE_SLIDING',
  CLOSET = 'CLOSET',
  OPEN_DOORWAY = 'OPEN_DOORWAY',
}

/**
 * What an opening sketch's `door type` holds: a built-in {@link DoorType} or an
 * organisation door (`c_<definitionId>`, see CustomOpeningTypeId).
 */
export type DoorGlyphType = DoorType | `c_${string}`;

/** The built-in window symbol (the only built-in window type). */
export const STANDARD_WINDOW_TYPE = 'STANDARD';

/** What an opening sketch's `window type` holds: the built-in window or an organisation window. */
export type WindowGlyphType = typeof STANDARD_WINDOW_TYPE | `c_${string}`;

/**
 * Subtype of a STRUCTURE annotation.
 * - BEAM: horizontal structural member hanging from the room ceiling
 * - COLUMN: vertical structural member rising from the room floor to the ceiling
 */
export enum StructureKind {
  BEAM = 'BEAM',
  COLUMN = 'COLUMN',
}

export enum AnnotationLabels {
  COORDINATE = 'COORDINATE',
  LENGTH = 'LENGTH',
  AREA = 'AREA',
  VOLUME = 'VOLUME',
}

interface IColor {
  a: number;
  b: number;
  g: number;
  r: number;
}
interface IPosition {
  x: number;
  y: number;
  z: number;
}
export interface ICadCommon {
  cadLayerId: string;
  cadObjectGroupId: string;
  identifier: string;
  name: string;
  color: IColor;
}
export interface ICadPoint extends ICadCommon {
  type: 'POINT';
  position: IPosition;
}
export interface ICadLine extends ICadCommon {
  type: 'LINE';
  points: Array<IPosition>;
}
export interface ICadPolygon extends ICadCommon {
  type: 'POLYGON';
  points: Array<IPosition>;
}
export type CadObject = ICadPoint | ICadLine | ICadPolygon;
